import React, { useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, PartyPopper, Users } from 'lucide-react';
import { Select } from '../../components/Controls';
import { Kpi } from '../attendance/shared';
import { WAITING } from './shared';

// Leave Calendar (Stephanet's leavemgmt/LeaveCalendar.jsx): monthly view of approved (and pending) leave, holidays and team availability

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const TYPE_COLOR = { Annual: '#2563eb', Sick: '#0d9488', Medical: '#0891b2', Casual: '#7c3aed', Maternity: '#db2777', Paternity: '#4f46e5', Unpaid: '#ea580c' };

export default function LeaveCalendar({ data, onView }) {
  const [month, setMonth] = useState(() => data.today.slice(0, 7));
  const [dept, setDept] = useState('');
  const [showPending, setShowPending] = useState(true);
  const [y, m] = month.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const days = new Date(y, m, 0).getDate();
  const lead = (first.getDay() + 6) % 7;
  const staff = data.employees.filter(e => !dept || e.department === dept).length;
  const reqs = useMemo(() => data.requests.filter(r => (r.status === 'approved' || (showPending && WAITING.includes(r.status)))
    && (!dept || r.department === dept) && r.start_date <= `${month}-31` && r.end_date >= `${month}-01`), [data, month, dept, showPending]);
  const shift = n => { const d = new Date(y, m - 1 + n, 1); setMonth(iso(d).slice(0, 7)); };
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(y, m - 1, d));
  while (cells.length % 7) cells.push(null);
  const onDay = s => reqs.filter(r => r.start_date <= s && r.end_date >= s);
  const workdays = cells.filter(c => c && c.getDay() % 6 !== 0 && !data.holiday_dates[iso(c)]);
  const peak = Math.max(0, ...workdays.map(c => onDay(iso(c)).filter(r => !WAITING.includes(r.status)).length));
  const hols = Object.entries(data.holiday_dates).filter(([d]) => d.startsWith(month));
  const label = first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  return (
    <>
      <div className="mdr-card att2-analysis-head">
        <div><div className="mds-card-title"><CalendarDays size={17} /> Leave Calendar</div><p>Monthly view of approved leaves, holidays, and team availability</p></div>
        <div className="att2-filters no-print">
          <Select value={dept} onChange={e => setDept(e.target.value)}><option value="">All Departments</option>{data.departments.map(d => <option key={d} value={d}>{d}</option>)}</Select>
          <label className="lvm-check"><input type="checkbox" checked={showPending} onChange={e => setShowPending(e.target.checked)} /> Show pending</label>
          <div className="lvm-monthnav">
            <button type="button" onClick={() => shift(-1)} aria-label="Previous month"><ChevronLeft size={16} /></button>
            <b>{label}</b>
            <button type="button" onClick={() => shift(1)} aria-label="Next month"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>
      <div className="md-kpis mds-kpis lvm-kpis">
        <Kpi label="Leaves This Month" value={reqs.filter(r => !WAITING.includes(r.status)).length} Ico={CalendarDays} tone="blue" note="Approved requests" />
        <Kpi label="Pending" value={reqs.filter(r => WAITING.includes(r.status)).length} Ico={CalendarDays} tone="amber" note="Awaiting approval" i={1} />
        <Kpi label="Holidays" value={hols.length} Ico={PartyPopper} tone="purple" note={hols.map(([, n]) => n).join(', ').slice(0, 40) || 'None'} i={2} />
        <Kpi label="Peak Absence" value={peak} Ico={Users} tone="red" note={`of ${staff} staff on one day`} i={3} />
      </div>
      <div className="mdr-card lvm-cal">
        <div className="lvm-cal-grid">
          {DOW.map(d => <div key={d} className="lvm-cal-dow">{d}</div>)}
          {cells.map((c, i) => {
            if (!c) return <div key={`x${i}`} className="lvm-cal-day off" />;
            const s = iso(c);
            const list = onDay(s);
            const hol = data.holiday_dates[s];
            const weekend = c.getDay() % 6 === 0;
            const away = list.filter(r => !WAITING.includes(r.status)).length;
            const pct = staff ? Math.round(100 * (staff - away) / staff) : 100;
            return (
              <div key={s} className={`lvm-cal-day ${weekend ? 'we' : ''} ${hol ? 'hol' : ''} ${s === data.today ? 'today' : ''}`}>
                <div className="lvm-cal-top"><b>{c.getDate()}</b>{!weekend && !hol && staff > 0 && <span className={pct < 80 ? 'low' : ''} title="Team availability">{pct}%</span>}</div>
                {hol && <div className="lvm-cal-hol"><PartyPopper size={11} /> {hol}</div>}
                {list.slice(0, 3).map(r => (
                  <button key={r.id} type="button" className={`lvm-cal-ev ${WAITING.includes(r.status) ? 'pend' : ''}`} style={{ '--c': TYPE_COLOR[r.leave_type] || '#64748b' }}
                          title={`${r.name} — ${r.leave_type} (${r.status})`} onClick={() => onView(r.id)}>{r.name.split(' ')[0]} · {r.leave_type}</button>
                ))}
                {list.length > 3 && <small className="lvm-cal-more">+{list.length - 3} more</small>}
              </div>
            );
          })}
        </div>
        <div className="lvm-legend">
          {Object.entries(TYPE_COLOR).map(([t, c]) => <span key={t}><i style={{ background: c }} /> {t}</span>)}
          <span><i className="pend" /> Pending</span><span><i className="hol" /> Holiday</span><span>% = team available that day</span>
        </div>
      </div>
    </>
  );
}
